import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * A toolbar palette.
 *
 * Stays open when you click something inside it — these hold transforms and
 * presets, which you use in bursts, and a menu that closes on every click turns
 * "try six things quickly" into twelve clicks. Closes on outside click or Esc.
 */
export function Popover({
  label,
  icon,
  badge,
  children,
  align = 'left',
  width = 'w-[34rem]',
  anchorId,
  compact = false,
}: {
  label: string;
  icon?: ReactNode;
  badge?: ReactNode;
  children: ReactNode;
  align?: 'left' | 'right';
  width?: string;
  /** `data-tour` value, so the tutorial can ring this control. */
  anchorId?: string;
  /** Icon only, no label or chevron — for tight toolbars. */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref} data-tour={anchorId}>
      <button
        className={clsx('btn', compact && 'btn-icon', open && 'btn-active')}
        title={compact ? label : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {icon}
        {!compact && label}
        {badge}
        {!compact && (
          <ChevronDown size={12} className={clsx('transition-transform', open && 'rotate-180')} />
        )}
      </button>
      {open && (
        <div
          className={clsx(
            'rise absolute top-full z-30 mt-1 max-h-[26rem] overflow-y-auto rounded-xl border border-ink-600 bg-ink-850 p-2 shadow-2xl shadow-black/60',
            width,
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
